import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Briefcase, MessageSquare, FileSearch, Send, Download, Loader2, Building2 } from 'lucide-react';
import { formatCurrency } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function EspaceExpert() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [demandes, setDemandes] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [newDemande, setNewDemande] = useState('');
  const [formats, setFormats] = useState({});

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/expert/dashboard`),
      axios.get(`${API_URL}/expert/formats`),
    ]).then(([d, f]) => { setDashboard(d.data); setFormats(f.data); }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const selectClient = async (client) => {
    setSelectedClient(client);
    const [mRes, dRes] = await Promise.all([
      axios.get(`${API_URL}/expert/messages/${client.structure_id}`),
      axios.get(`${API_URL}/expert/demandes/${client.structure_id}`),
    ]);
    setMessages(mRes.data); setDemandes(dRes.data);
  };

  const sendMessage = async () => {
    if (!newMsg.trim() || !selectedClient) return;
    try {
      await axios.post(`${API_URL}/expert/messages`, { structure_id: selectedClient.structure_id, contenu: newMsg, type: 'message' });
      setNewMsg('');
      const res = await axios.get(`${API_URL}/expert/messages/${selectedClient.structure_id}`);
      setMessages(res.data);
    } catch (e) { toast.error('Erreur envoi'); }
  };

  const sendDemande = async () => {
    if (!newDemande.trim() || !selectedClient) return;
    try {
      await axios.post(`${API_URL}/expert/demandes`, { structure_id: selectedClient.structure_id, description: newDemande });
      toast.success('Demande envoyée');
      setNewDemande('');
      const res = await axios.get(`${API_URL}/expert/demandes/${selectedClient.structure_id}`);
      setDemandes(res.data);
    } catch (e) { toast.error('Erreur'); }
  };

  const handleExport = async (structureId, format) => {
    try {
      const res = await axios.get(`${API_URL}/expert/export/${structureId}/${format}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = `export_${format}.${formats[format]?.ext || 'txt'}`; a.click();
      toast.success(`Export ${format} téléchargé`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur export'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;
  if (!dashboard) return <div className="text-center py-12 text-slate-500">Espace réservé aux experts-comptables</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold"><Briefcase className="w-6 h-6 inline mr-2" />Espace expert-comptable</h1>
          <p className="text-slate-500">{dashboard.expert?.cabinet_nom || ''} — {dashboard.clients?.length || 0} client(s)</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Client list */}
        <div className="lg:col-span-1 space-y-3">
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">Mes clients</h2>
          {(dashboard.clients || []).map(c => (
            <Card key={c.structure_id} className={`cursor-pointer transition-all hover:shadow-md ${selectedClient?.structure_id === c.structure_id ? 'border-[#0F5156] border-2' : ''}`} onClick={() => selectClient(c)}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-sm">{c.nom}</p>
                    <p className="text-xs text-slate-400 font-mono">{c.siret}</p>
                  </div>
                  <div className="text-right">
                    {c.ecritures_a_valider > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px]">{c.ecritures_a_valider} écritures</Badge>}
                    {c.messages_non_lus > 0 && <Badge className="bg-blue-100 text-blue-700 text-[10px] ml-1">{c.messages_non_lus} msg</Badge>}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Client detail */}
        <div className="lg:col-span-2">
          {selectedClient ? (
            <Tabs defaultValue="messages">
              <TabsList>
                <TabsTrigger value="messages"><MessageSquare className="w-4 h-4 mr-1" /> Messages</TabsTrigger>
                <TabsTrigger value="demandes"><FileSearch className="w-4 h-4 mr-1" /> Demandes pièces</TabsTrigger>
                <TabsTrigger value="exports"><Download className="w-4 h-4 mr-1" /> Exports</TabsTrigger>
              </TabsList>

              <TabsContent value="messages">
                <Card>
                  <CardHeader><CardTitle>Échanges — {selectedClient.nom}</CardTitle></CardHeader>
                  <CardContent>
                    <ScrollArea className="h-[300px] pr-4 mb-4">
                      {messages.length === 0 ? <p className="text-center text-slate-400 py-8">Aucun message</p> : messages.map(m => (
                        <div key={m.id} className={`mb-3 flex ${m.user_role === 'expert_comptable' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[70%] rounded-xl px-4 py-2 ${m.user_role === 'expert_comptable' ? 'bg-[#0F5156] text-white' : 'bg-slate-100 text-slate-900'}`}>
                            <p className="text-xs opacity-70 mb-1">{m.user_name}</p>
                            <p className="text-sm">{m.contenu}</p>
                            <p className="text-[10px] opacity-50 mt-1">{new Date(m.created_at).toLocaleString('fr-FR')}</p>
                          </div>
                        </div>
                      ))}
                    </ScrollArea>
                    <div className="flex gap-2">
                      <Input value={newMsg} onChange={e => setNewMsg(e.target.value)} placeholder="Votre message..." onKeyDown={e => e.key === 'Enter' && sendMessage()} />
                      <Button onClick={sendMessage} className="bg-[#0F5156] hover:bg-[#0d464a]"><Send className="w-4 h-4" /></Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="demandes">
                <Card>
                  <CardHeader><CardTitle>Demandes de pièces</CardTitle></CardHeader>
                  <CardContent>
                    <div className="flex gap-2 mb-4">
                      <Input value={newDemande} onChange={e => setNewDemande(e.target.value)} placeholder="Décrivez la pièce demandée..." />
                      <Button onClick={sendDemande} className="bg-[#0F5156] hover:bg-[#0d464a]"><FileSearch className="w-4 h-4 mr-2" />Demander</Button>
                    </div>
                    <Table><TableHeader><TableRow><TableHead>Description</TableHead><TableHead>Date</TableHead><TableHead>Statut</TableHead></TableRow></TableHeader>
                    <TableBody>{demandes.map(d => (
                      <TableRow key={d.id}><TableCell>{d.description}</TableCell><TableCell className="text-sm">{d.created_at?.slice(0, 10)}</TableCell>
                        <TableCell><Badge className={d.statut === 'fournie' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>{d.statut === 'fournie' ? 'Fournie' : 'En attente'}</Badge></TableCell>
                      </TableRow>
                    ))}</TableBody></Table>
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="exports">
                <Card>
                  <CardHeader><CardTitle>Exports comptables</CardTitle><CardDescription>Exportez les écritures validées de {selectedClient.nom}</CardDescription></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      {Object.entries(formats).map(([key, fmt]) => (
                        <Button key={key} variant="outline" className="h-16 flex-col gap-1" onClick={() => handleExport(selectedClient.structure_id, key)}>
                          <Download className="w-5 h-5" /><span className="text-sm">{fmt.label}</span>
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          ) : (
            <Card><CardContent className="py-16 text-center text-slate-400"><Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" /><p>Sélectionnez un client</p></CardContent></Card>
          )}
        </div>
      </div>
    </div>
  );
}
